/**
 * Consistent ordering for BIM/CAD (and related) filenames on
 * deliverable pages and VirtualSpace file rails.
 *
 * Heuristic: infer a **vertical floor index** from the basename
 * (basement → low levels → high levels → roof), then a **sheet
 * kind** rank (floor plan before ceiling/RCP, then elevations,
 * sections, 3D, …), optional **drawing sheet** key (A-101), then
 * `localeCompare` with `numeric: true`, then optional **created_at**
 * tie-break.
 *
 * Optional **display_sort_key** on file rows overrides the whole
 * heuristic when set (see `compareBimCadFileOrder`).
 */

import { archiveDisplayBaseName } from "@/lib/virtualspace/deliverable-archive-naming";

/** Sentinel: no recognizable floor — sort after known floors. */
export const FLOOR_UNKNOWN = 500_000;
/** Roof / penthouse / roof-plan sheets — after typical level numbers. */
const FLOOR_ROOF_BAND = 900_000;
/** Parking deck band — deeper P2 before P1 (more negative = lower). */
const FLOOR_PARKING_BASE = -220;

/**
 * Human-readable band title for UI sub-headings (file lists).
 */
export function floorBandLabel(fileName: string | null | undefined): string {
  const base = archiveDisplayBaseName(fileName);
  if (!base.trim()) return "Other";
  const lower = base.trim().toLowerCase();
  const subCellar = /\bsub[-\s]?cellar(?:\s+(\d+))?\b/i.exec(lower);
  if (subCellar) {
    return subCellar[1] ? `Sub-cellar ${subCellar[1]}` : "Sub-cellar";
  }

  const v = floorVerticalIndex(base);
  if (v >= FLOOR_UNKNOWN) return "Other";
  if (v >= FLOOR_ROOF_BAND) return "Roof & penthouse";
  if (v <= FLOOR_PARKING_BASE + 50 && v < -100) return "Parking & lower levels";
  if (v < 0) return "Basement & below-grade";
  if (v === 0) return "Ground floor";
  return `Level ${v}`;
}

export type DrawingSheetKey = { disc: string; num: number };

const DISC_ORDER: Record<string, number> = {
  G: 1,
  A: 2,
  S: 3,
  M: 4,
  E: 5,
  P: 6,
  L: 7,
  I: 8,
  T: 9,
  C: 10,
  D: 11,
  F: 12,
};

function disciplineSortOrder(disc: string): number {
  const u = disc.toUpperCase().slice(0, 1);
  return DISC_ORDER[u] ?? 40 + u.charCodeAt(0);
}

/**
 * Parse a leading drawing id like A-101, M201 from the last
 * hyphen-separated segment (common on Revit exports:
 * "Project — A101 — Floor plan").
 */
export function parseDrawingSheetKey(
  fileName: string | null | undefined,
): DrawingSheetKey | null {
  const base = archiveDisplayBaseName(fileName);
  if (!base.trim()) return null;
  const segments = base.split(/\s*[-–—]\s+/).map((s) => s.trim());
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i];
    let m = /^([A-Za-z]{1,2})\s*[-_.]?\s*(\d{2,4})\b/.exec(seg);
    if (m && m[1].length <= 2)
      return { disc: m[1].toUpperCase(), num: parseInt(m[2], 10) };
    m = /^([A-Za-z]{1,2})(\d{3,4})\b/.exec(seg);
    if (m && m[1].length <= 2)
      return { disc: m[1].toUpperCase(), num: parseInt(m[2], 10) };
  }
  const first = segments[0];
  let tail = /^([A-Za-z]{1,2})\s*[-_.]?\s*(\d{2,4})\b/.exec(first);
  if (tail && tail[1].length <= 2)
    return { disc: tail[1].toUpperCase(), num: parseInt(tail[2], 10) };
  const anywhere = /\b([A-Za-z])[-_.](\d{2,4})\b/.exec(base);
  if (anywhere) {
    return {
      disc: anywhere[1].toUpperCase(),
      num: parseInt(anywhere[2], 10),
    };
  }
  return null;
}

function compareDrawingSheetKeys(a: string, b: string): number {
  const da = parseDrawingSheetKey(a);
  const db = parseDrawingSheetKey(b);
  if (da && db) {
    const oa = disciplineSortOrder(da.disc);
    const ob = disciplineSortOrder(db.disc);
    if (oa !== ob) return oa - ob;
    if (da.num !== db.num) return da.num - db.num;
    return 0;
  }
  if (da && !db) return -1;
  if (!da && db) return 1;
  return 0;
}

function compareIsoCreated(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const ta = a ? new Date(a).getTime() : 0;
  const tb = b ? new Date(b).getTime() : 0;
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  if (ta !== tb) return ta - tb;
  return 0;
}

/**
 * Lower values = lower in the building (closer to below-grade).
 * Matches common Revit / AutoCAD export naming.
 */
export function floorVerticalIndex(fileName: string | null | undefined): number {
  const base = archiveDisplayBaseName(fileName);
  if (!base.trim()) return FLOOR_UNKNOWN + 1;
  const s = base.trim();
  const lower = s.toLowerCase();

  if (/\bsub[-\s]?basement\b/i.test(s)) return -120;

  // Sub-cellar / sub cellar (common NYC office tower exports) — deeper with higher tier #
  const subCellarM = /\bsub[-\s]?cellar(?:\s+(\d+))?\b/i.exec(lower);
  if (subCellarM) {
    const tier = subCellarM[1] ? parseInt(subCellarM[1], 10) : 1;
    return -128 - tier * 8;
  }

  if (/\bbasement\b/i.test(s)) return -100;

  // Level B1 / Level B2 (below-grade level index)
  let m = /\blevel\s+b[-]?(\d+)\b/i.exec(lower);
  if (m) return -parseInt(m[1], 10);

  m = /\blevel\s+(\d+)\b/i.exec(s);
  if (m) return parseInt(m[1], 10);
  m = /\bfloor\s+(\d+)\b/i.exec(s);
  if (m) return parseInt(m[1], 10);

  if (/\blower\s+level\b/i.test(lower)) return -80;

  // Parking / garage decks — assume higher P# = deeper (lower in building)
  if (/(?:parking|garage|p\/d)\b/i.test(lower)) {
    const pm = /\bp[-.\s]?(\d+)\b/i.exec(lower);
    if (pm) return FLOOR_PARKING_BASE - parseInt(pm[1], 10);
  }

  m = /\b(?:ground|g\.?)\s*floor\b/i.exec(lower);
  if (m) return 0;
  if (/\blower\s+ground\b|\blg\b/i.test(lower)) return 0;
  if (/\bupper\s+ground\b|\bug\b/i.test(lower)) return 1;
  if (/\b1(?:st)?\s*floor\b/i.test(lower) || /\bfirst\s+floor\b/i.test(lower))
    return 1;

  if (/\bmezzanine\b|\bmezz\b/i.test(lower)) return 2;

  // Short "L11" / "L-11" style (word boundary; avoid matching inside tokens)
  m = /(?:^|[^a-z0-9])l\s*-?\s*(\d+)\b/i.exec(lower);
  if (m) return parseInt(m[1], 10);

  if (/\broof\s+plan\b/i.test(lower) || /\b(?:penthouse|ph)\b/i.test(lower))
    return FLOOR_ROOF_BAND;
  if (/\broof\b/i.test(lower)) return FLOOR_ROOF_BAND;

  return FLOOR_UNKNOWN;
}

/** Lower = earlier within the same inferred floor. */
export function sheetKindRank(fileName: string | null | undefined): number {
  const base = archiveDisplayBaseName(fileName);
  if (!base.trim()) return 100;
  const l = base.toLowerCase();

  if (/\b(?:key|index|site|overall)\s*plan\b/i.test(base) || /\bsite\s*plan\b/i.test(l))
    return 5;

  if (/\brcp\b/i.test(l) || l.includes("reflected ceiling")) return 20;
  if (l.includes("ceiling plan")) return 20;

  if (l.includes("floor plan")) return 10;
  if (/\bplan\b/i.test(l) && !l.includes("ceiling") && !l.includes("roof"))
    return 12;

  if (l.includes("finish") || l.includes("furniture")) return 18;
  if (l.includes("demolition") || /\bdemo\b/.test(l)) return 19;

  if (l.includes("elevation")) return 40;
  if (l.includes("section")) return 50;
  if (l.includes("schedule") || l.includes("legend") || l.includes("cover"))
    return 55;
  if (l.includes("detail")) return 60;
  if (/\b3d\b/i.test(base) || l.includes("axon") || l.includes("perspective"))
    return 70;
  if (l.includes("sheet") || /^a\d{2,3}[-_]/i.test(base.trim())) return 80;

  return 100;
}

export type BimCadFileOrderInput = {
  file_name?: string | null;
  /** Alias used by APS / share URN objects */
  fileName?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
  display_sort_key?: number | null;
};

function sortableName(x: BimCadFileOrderInput): string {
  return x.file_name ?? x.fileName ?? "";
}

/**
 * Full comparator for DB-backed file rows and share URN entries.
 * `display_sort_key` (when set on either row) pins manual order:
 * all keyed rows sort before unkeyed; among keyed, ascending key.
 */
export function compareBimCadFileOrder(a: BimCadFileOrderInput, b: BimCadFileOrderInput): number {
  const ka = a.display_sort_key;
  const kb = b.display_sort_key;
  const aKeyed = ka != null && Number.isFinite(ka);
  const bKeyed = kb != null && Number.isFinite(kb);
  if (aKeyed && bKeyed && ka !== kb) return ka - kb;
  if (aKeyed && !bKeyed) return -1;
  if (!aKeyed && bKeyed) return 1;
  return compareBimCadFileNames(sortableName(a), sortableName(b), {
    aCreated: a.created_at ?? a.createdAt,
    bCreated: b.created_at ?? b.createdAt,
  });
}

/**
 * Comparator for file rows / URNs — use wherever we previously
 * sorted only by `localeCompare` on `file_name`.
 */
export type FloorBandRow<T> =
  | { kind: "band"; label: string }
  | { kind: "row"; item: T };

/**
 * Inserts soft "floor band" markers when the inferred level changes.
 * Skips bands for short lists (default min 6) to avoid noise.
 */
export function interleaveFloorBands<T>(
  items: T[],
  getFileName: (row: T) => string | null | undefined,
  opts?: { minItems?: number },
): FloorBandRow<T>[] {
  const min = opts?.minItems ?? 6;
  if (items.length < min) {
    return items.map((item) => ({ kind: "row" as const, item }));
  }
  const out: FloorBandRow<T>[] = [];
  let prev: string | null = null;
  for (const item of items) {
    const label = floorBandLabel(getFileName(item) ?? null);
    if (prev === null || label !== prev) {
      out.push({ kind: "band", label });
      prev = label;
    }
    out.push({ kind: "row", item });
  }
  return out;
}

export function compareBimCadFileNames(
  aName: string | null | undefined,
  bName: string | null | undefined,
  options?: { aCreated?: string | null; bCreated?: string | null },
): number {
  const a = aName ?? "";
  const b = bName ?? "";
  const fa = floorVerticalIndex(a);
  const fb = floorVerticalIndex(b);
  if (fa !== fb) return fa - fb;
  const ka = sheetKindRank(a);
  const kb = sheetKindRank(b);
  if (ka !== kb) return ka - kb;
  const ds = compareDrawingSheetKeys(a, b);
  if (ds !== 0) return ds;
  const loc = a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });
  if (loc !== 0) return loc;
  if (options && (options.aCreated || options.bCreated)) {
    return compareIsoCreated(options.aCreated, options.bCreated);
  }
  return 0;
}
